import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  compact,
  find,
  filter,
  every,
  get,
  includes,
  isEqual,
  map,
  set,
  uniq,
  merge,
  isNil,
} from 'lodash';

import { observableCombineLatest, rx, Subscription } from '@shared/rx';
import {
  IFieldPreferences,
  SelectOption,
  ISelectOptionsType,
} from '@shared/types';
import { ValidationService } from '../../form/services/validation/validation.service';
import { asObservable, asSelectOptions } from '@shared/utils';
import { onControlValueChanged } from '@shared/rx-operators';
import { StateService } from '@app/shared/services';
import { I18n } from '@app/i18n/strings-keys';

@Component({
  selector: 'xux-input-checkbox-group',
  templateUrl: 'xux-input-checkbox-group.component.html',
  styleUrls: ['xux-input-checkbox-group.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxInputCheckboxGroupComponent implements OnInit, OnDestroy {
  @Input() public field: IFieldPreferences = {};
  public blockTitle: string;
  public showSelectAll: boolean = true;
  public twoColumnLayout: boolean = false;
  public options: ISelectOptionsType[] = [];
  public subscriptions: Subscription[] = [];
  public selectAllLinkText: string;
  private defaultValue: string[] = [];

  public staticTextContent = {
    selectAll: I18n.Common.SelectAll,
    deselectAll: I18n.Common.DeselectAll,
  };

  public constructor(
    private readonly validationService: ValidationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly stateService: StateService,
  ) {}

  public ngOnInit(): void {
    this.blockTitle = this.field?.widgetData?.blockTitle;
    this.staticTextContent = merge(
      {},
      this.staticTextContent,
      this.field?.widgetData?.i18n ?? {},
    );
    this.showSelectAll = get(this.field, 'widgetData.showSelectAll', true);
    this.twoColumnLayout = get(this.field, 'widgetData.twoColumnLayout', false);
    this.subscriptions.push(
      asObservable(this.field.defaultValue).subscribe((defaultValue) => {
        this.defaultValue = defaultValue || [];
        this.setValue(this.defaultValue);
      }),
    );
    this.subscriptions.push(
      observableCombineLatest([
        asSelectOptions(this.field.selectOptions).pipe(
          rx.tap((selectOptions) => this.addOptionValidator(selectOptions)),
        ),
        onControlValueChanged(this.field.control),
        this.stateService.select(['stores', 'isStoresRequestInProgress']).pipe(
          rx.filter((isStoresRequestInProgress) => !isStoresRequestInProgress),
          rx.first(),
        ),
      ])
        .pipe(
          rx.filter(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ([selectOptions, selectedItems, isStoresRequestInProgress]) =>
              !isStoresRequestInProgress,
          ),
        )
        .subscribe(([selectOptions, selectedItems]) => {
          const newOptions: SelectOption[] = selectOptions
            .map(({ value, text, hidden, disabled }) => {
              const oldOptions = find(this.options, { value });

              return isNil(oldOptions)
                ? { value, text, hidden, disabled }
                : merge({}, oldOptions, { disabled });
            })
            .map((option: SelectOption) =>
              set(option, 'selected', includes(selectedItems, option.value)),
            );
          // The following lines have been intentionally written this way.
          // ngFor directive redraws all items in the array when array reference is chanhed.
          // To sustain transitions we are working with the very same array instance
          this.options.length = 0;
          this.options.push(...newOptions);
          this.changeDetectorRef.markForCheck();
          this.setSelectAllLinkText();
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public itemClicked(
    { target }: MouseEvent & { target: HTMLInputElement },
    value,
  ): void {
    if (this.field.control) {
      const oldValue: string[] = this.field.control.value || [];
      let newValue: string[] = target.checked
        ? uniq(oldValue.concat([value]))
        : oldValue.filter((val) => val !== value);
      newValue = newValue.filter((val) => !!find(this.options, { value: val }));

      this.setValue(newValue);
    }
    this.setSelectAllLinkText();
  }

  public selectAll(): void {
    if (this.field.control) {
      let allValues = map(this.options, 'value');
      const isAllChecked = this.isAllChecked();
      // If deselect all, need exclude disabled items
      if (isAllChecked) {
        allValues = map(filter(this.options, 'disabled'), 'value');
      }
      const newValue = isEqual(this.field.control.value, allValues)
        ? []
        : allValues;
      this.setValue(newValue);
    }
    this.setSelectAllLinkText();
  }

  private addOptionValidator(items: SelectOption[]): void {
    if (this.field.control) {
      const values: string[] = map(items, 'value');

      setTimeout(() => {
        this.field.control.setValidators([
          ...compact(get(this.field, 'validators', [])),
          this.validationService.contains(values),
        ]);

        this.field.control.updateValueAndValidity();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  private setValue(value): void {
    this.field.control.markAsDirty();
    this.field.control.markAsTouched();
    this.field.control.setValue(value);
  }

  private setSelectAllLinkText(): void {
    this.selectAllLinkText = this.isAllChecked()
      ? this.staticTextContent.deselectAll
      : this.staticTextContent.selectAll;
  }

  private isAllChecked(): boolean {
    const allOptionsWithoutHidden: SelectOption[] = filter(
      this.options,
      (option) => !option.hidden,
    );

    return (
      allOptionsWithoutHidden.length &&
      every(allOptionsWithoutHidden, 'selected')
    );
  }
}
